import {announce} from '@react-aria/live-announcer'
import Big from 'big.js'

import useDiscountAmount from '@/hooks/useDiscountAmount'
import {isFoodSupportedCountry} from '@/lib/food-grubhub/config'
import {getTipsAmount, useTips} from '@/lib/food-grubhub/store'
import {useCountry, useCurrencySymbol} from '@/lib/locale/country'
import {useLocale} from '@/lib/locale/locale'
import useCashback from '@/lib/payment/hooks/useCashback'
import {getPromotionCodeInfo} from '@/lib/payment/paymentService'
import {useRoundUp} from '@/lib/payment/store'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'

import {useFoodGrubhubCart} from './useFoodGrubhubCart'

export default function useFoodGrubhubCartSummary(
  isRewardsApplied: boolean,
  coupon: string,
  setCoupon?: (coupon: string) => void | Promise<void>,
) {
  const [locale] = useLocale()
  const currencySymbol = useCurrencySymbol()
  const latestCurrencySymbol = useLatest(currencySymbol)

  const [country] = useCountry()

  invariant(isFoodSupportedCountry(country), 'Country is not supported')

  const {data: foodGrubhubCart, isLoading, isFetching} = useFoodGrubhubCart(locale)

  const [roundUp] = useRoundUp()
  const [tips] = useTips()
  const tipsAmount = getTipsAmount(tips, country)

  console.log(tipsAmount)

  const couponRef = useRef<string>('')
  const [couponIsDirty, setCouponIsDirty] = useState(false)
  const handleCouponBlur = useCallback(() => {
    void setCoupon?.(couponRef.current)
  }, [setCoupon])

  const {
    data: couponInfo,
    error: couponError,
    isLoading: isCouponLoading,
    isFetching: isCouponFetching,
  } = useQuery({
    queryKey: ['!promotionCodeInfo', coupon],
    queryFn: async () => getPromotionCodeInfo(coupon),
    enabled: !!coupon && !isRewardsApplied,
    throwOnError: false,
    ...NO_REFETCH_OPTIONS,
  })

  useEffect(
    function announceCouponSuccess() {
      if (couponInfo) {
        announce(`Coupon applied: -${latestCurrencySymbol.current}${couponInfo.amount}`)
      }
    },
    [couponInfo, couponInfo?.code],
  )

  useEffect(
    function announceCouponError() {
      if (couponError === null) return
      announce(couponError.message)
    },
    [couponError, couponError?.message],
  )

  const handleCouponChange = useCallback(
    (value: string) => {
      couponRef.current = value
      if (value === '') {
        setCouponIsDirty(false)
      } else {
        setCouponIsDirty(true)
      }
      void setCoupon?.('')
    },
    [setCoupon],
  )

  const cartItemNumber = foodGrubhubCart?.foodCartItems.length ?? 0

  const subtotal = Big(foodGrubhubCart?.moreInfo?.charges?.dinerSubtotal ?? 0)
  const deliveryFee = Big(foodGrubhubCart?.moreInfo?.charges?.fees.delivery ?? 0)
  const serviceFee = Big(foodGrubhubCart?.moreInfo?.charges?.fees.service ?? 0)
  const totalBeforeTax = Big(subtotal).plus(deliveryFee).plus(serviceFee)

  // TODO: Should include `added_fees`, `misc_fees`

  const salesTax = Big(foodGrubhubCart?.moreInfo?.charges?.taxes.total ?? 0)

  const totalAfterTax = totalBeforeTax.plus(salesTax).plus(tipsAmount)

  // TODO: handle tip

  const roundUpAmount = totalAfterTax.round(0, Big.roundUp).minus(totalAfterTax)

  const totalAfterRoundUp = roundUp ? totalAfterTax.plus(roundUpAmount) : totalAfterTax

  const {data: {discount: discountValue} = {discount: 0}} = useDiscountAmount(totalAfterRoundUp)
  const derivedDiscountValue = (() => {
    if (isRewardsApplied) {
      return discountValue
    }

    if (couponInfo) {
      return couponInfo.amount
    }

    return 0
  })()

  let total = totalAfterRoundUp.minus(derivedDiscountValue)
  if (total.lt(0)) {
    total = Big(0)
  }

  const {data: cashback} = useCashback(total)

  return {
    cashback,
    cartItemNumber,
    total,
    totalBeforeTax,
    tax: salesTax,
    discountValue,
    derivedDiscountValue,
    subtotal,
    cart: foodGrubhubCart,
    handleCouponBlur,
    couponError,
    handleCouponChange,
    couponInfo,
    isCouponLoading,
    isLoading: isLoading || isCouponLoading,
    isFetching: isFetching || isCouponFetching,
    coupon,
    couponIsDirty,
    totalAfterTax,
    roundUpAmount,
    totalAfterRoundUp,
    deliveryFee,
    serviceFee,
    tips: tipsAmount,
  }
}
